import { upperCamelCase } from '@art-suite/art-core-ts';

import { ContractorIdsByTrade } from '@kaiizen/kai-scan-template-system';

import { getTaskTradeForContractorAssignment } from '@/Client/Lib/contractorTradeLib';

export type TradeSettingsTrade = {
  displayName?: string;
  isEnabled?: boolean;
  sortOrder?: number;
};

type TradeSourceTask = {
  trade?: string | null;
  template?: {
    trade?: string | null;
  } | null;
};

type TradeSourceGroup = {
  tasks?: TradeSourceTask[] | null;
};

type OverviewTradeSource = {
  groups?: TradeSourceGroup[] | null;
  defaultContractorsByTrade?: ContractorIdsByTrade;
};

const getConfiguredTradeEntries = (
  configuredTrades?: Record<string, TradeSettingsTrade>,
) =>
  Object.entries(configuredTrades ?? {})
    .sort(([leftSlug, leftTrade], [rightSlug, rightTrade]) => {
      if (leftSlug === 'general') return -1;
      if (rightSlug === 'general') return 1;

      const leftSortOrder = leftTrade.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const rightSortOrder = rightTrade.sortOrder ?? Number.MAX_SAFE_INTEGER;
      if (leftSortOrder !== rightSortOrder) return leftSortOrder - rightSortOrder;

      return (leftTrade.displayName || leftSlug).localeCompare(
        rightTrade.displayName || rightSlug,
      );
    });

const pushTrade = (trades: Set<string>, trade?: string | null) => {
  if (!trade) return;
  const normalizedTrade = trade.trim();
  if (!normalizedTrade) return;
  trades.add(normalizedTrade);
};

const getFallbackTradesFromScan = (
  scanRoot?: OverviewTradeSource | null,
) => {
  const fallbackTrades = new Set<string>();
  const assignedTradeKeys = Object.keys(scanRoot?.defaultContractorsByTrade ?? {});

  scanRoot?.groups?.forEach(group => {
    group.tasks?.forEach(task => {
      pushTrade(fallbackTrades, getTaskTradeForContractorAssignment(task));
    });
  });

  assignedTradeKeys.forEach(trade => {
    pushTrade(fallbackTrades, trade);
  });

  return Array.from(fallbackTrades);
};

export const getOverviewTradeOrder = ({
  configuredTrades,
  scanRoot,
}: {
  configuredTrades?: Record<string, TradeSettingsTrade>;
  scanRoot?: OverviewTradeSource | null;
}) => {
  const scanTrades = getFallbackTradesFromScan(scanRoot);
  if (configuredTrades && Object.keys(configuredTrades).length > 0) {
    const configuredTradeSet = new Set(Object.keys(configuredTrades));
    const orderedConfiguredTrades = getConfiguredTradeEntries(configuredTrades)
      .map(([trade]) => trade)
      .filter(trade => scanTrades.includes(trade));
    const remainingScanTrades = scanTrades.filter(trade => !configuredTradeSet.has(trade));

    return [...orderedConfiguredTrades, ...remainingScanTrades];
  }

  return scanTrades;
};

export const getTradeDisplayName = (
  trade: string,
  configuredTrades?: Record<string, TradeSettingsTrade>,
) => configuredTrades?.[trade]?.displayName || upperCamelCase(trade, ' ');
